/**
 * @coeldery/document-scan / warp.ts —— **透視矯正**（homography ＋ 雙線性取樣；純函數、零依賴）
 *
 * 把偵測到嘅四邊形（[tl,tr,br,bl]）拉平成正面矩形（好似影印／掃描出嚟）。
 * 計法：由**目標矩形 → 來源四角**嘅單應矩陣（dst→src），逐個輸出像素反查來源座標取樣。
 */
import type { Point, Raster } from './detect.ts';

export interface WarpResult { data: Uint8ClampedArray; width: number; height: number }

/** 3×3 單應矩陣（row-major，h33 = 1），令 `dst = H·src`（齊次座標）*/
export function homography(src: Point[], dst: Point[]): number[] {
  const A: number[][] = [];
  const b: number[] = [];
  for (let i = 0; i < 4; i++) {
    const { x: X, y: Y } = src[i];
    const { x: u, y: v } = dst[i];
    A.push([X, Y, 1, 0, 0, 0, -u * X, -u * Y]); b.push(u);
    A.push([0, 0, 0, X, Y, 1, -v * X, -v * Y]); b.push(v);
  }
  const h = solve(A, b);
  return [h[0], h[1], h[2], h[3], h[4], h[5], h[6], h[7], 1];
}

/** 高斯-約旦消去解 8×8（A 會被改；回解）*/
function solve(A: number[][], b: number[]): number[] {
  const n = b.length;
  const M = A.map((row, i) => [...row, b[i]]);
  for (let c = 0; c < n; c++) {
    let piv = c;
    for (let r = c + 1; r < n; r++) if (Math.abs(M[r][c]) > Math.abs(M[piv][c])) piv = r;
    const tmp = M[c]; M[c] = M[piv]; M[piv] = tmp;
    const p = M[c][c] || 1e-9;
    for (let k = c; k <= n; k++) M[c][k] /= p;
    for (let r = 0; r < n; r++) {
      if (r === c) continue;
      const f = M[r][c];
      if (!f) continue;
      for (let k = c; k <= n; k++) M[r][k] -= f * M[c][k];
    }
  }
  return M.map((row) => row[n]);
}

/** 目標矩形尺寸（用對邊較長者，避免拉扁）*/
export function quadSize(corners: Point[]): { width: number; height: number } {
  const d = (a: Point, b: Point) => Math.hypot(a.x - b.x, a.y - b.y);
  const w = Math.max(d(corners[0], corners[1]), d(corners[3], corners[2]));
  const h = Math.max(d(corners[0], corners[3]), d(corners[1], corners[2]));
  return { width: Math.max(1, Math.round(w)), height: Math.max(1, Math.round(h)) };
}

/** 透視矯正：`src` 按 `corners`（[tl,tr,br,bl]）拉平成矩形（長邊 ≤ maxEdge）*/
export function warpQuad(src: Raster, corners: Point[], opts: { maxEdge?: number } = {}): WarpResult {
  const maxEdge = opts.maxEdge ?? 1600;
  const size = quadSize(corners);
  const scale = Math.min(1, maxEdge / Math.max(size.width, size.height));
  const width = Math.max(1, Math.round(size.width * scale));
  const height = Math.max(1, Math.round(size.height * scale));
  const dst: Point[] = [
    { x: 0, y: 0 }, { x: width - 1, y: 0 }, { x: width - 1, y: height - 1 }, { x: 0, y: height - 1 },
  ];
  const H = homography(dst, corners);   // dst → src
  const out = new Uint8ClampedArray(width * height * 4);
  const { data, width: sw, height: sh } = src;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const dz = H[6] * x + H[7] * y + H[8] || 1e-9;
      const sx = (H[0] * x + H[1] * y + H[2]) / dz;
      const sy = (H[3] * x + H[4] * y + H[5]) / dz;
      sample(data, sw, sh, sx, sy, out, (y * width + x) * 4);
    }
  }
  return { data: out, width, height };
}

/** 雙線性取樣（越界 → 黑，alpha 255）*/
function sample(
  data: Uint8ClampedArray | Uint8Array, w: number, h: number,
  x: number, y: number, out: Uint8ClampedArray, o: number,
): void {
  if (x < 0 || y < 0 || x > w - 1 || y > h - 1) {
    out[o] = out[o + 1] = out[o + 2] = 0; out[o + 3] = 255; return;
  }
  const x0 = Math.floor(x), y0 = Math.floor(y);
  const x1 = Math.min(w - 1, x0 + 1), y1 = Math.min(h - 1, y0 + 1);
  const fx = x - x0, fy = y - y0;
  const idx = (xx: number, yy: number) => (yy * w + xx) * 4;
  for (let c = 0; c < 3; c++) {
    const p00 = data[idx(x0, y0) + c], p10 = data[idx(x1, y0) + c];
    const p01 = data[idx(x0, y1) + c], p11 = data[idx(x1, y1) + c];
    out[o + c] = (p00 * (1 - fx) + p10 * fx) * (1 - fy) + (p01 * (1 - fx) + p11 * fx) * fy;
  }
  out[o + 3] = data[idx(x0, y0) + 3];
}
